import React from 'react';
import { Link } from 'react-router-dom';
import type { Division, Category } from '@sulis-liiga/common';
import { DivisionStatus } from '@sulis-liiga/common';
import { CATEGORY_TO_SLUG } from '../../utils/category';

interface DivisionListProps {
  divisions: Division[];
  groupByCategory?: boolean;
  // When both are provided, each division shows a link to its standings page.
  leagueId?: string;
  seasonId?: string;
  // When true, each division also shows an "Edit settings" link (admins only).
  // Division settings are edited from here (the league detail page), not from
  // the per-category Manage page (which handles team membership only).
  canManage?: boolean;
  // When true, render each division as a compact single-line banner (grouped by
  // category) in the style of a game banner, instead of the full detail card.
  compact?: boolean;
}

const DivisionList: React.FC<DivisionListProps> = ({
  divisions,
  groupByCategory = true,
  leagueId,
  seasonId,
  canManage = false,
  compact = false,
}) => {
  // The per-category placement-queue route (admins pick teams into tiers there).
  const manageTeamsHref = (division: Division) =>
    `/leagues/${leagueId}/divisions/${
      CATEGORY_TO_SLUG[division.category]
    }/manage?seasonId=${seasonId}`;

  const renderDivisionActions = (division: Division) =>
    leagueId && seasonId ? (
      <div className="mt-4 flex items-center gap-2">
        <Link
          to={`/leagues/${leagueId}/seasons/${seasonId}/divisions/${division.id}/standings`}
          className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
        >
          Standings
        </Link>
        {canManage && (
          <>
            <Link
              to={manageTeamsHref(division)}
              className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
            >
              Manage
            </Link>
            <Link
              to={`/leagues/${leagueId}/seasons/${seasonId}/divisions/${division.id}`}
              className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-primary-600 border border-primary-600 hover:bg-primary-50 transition-colors"
            >
              Edit
            </Link>
          </>
        )}
      </div>
    ) : null;

  const getStatusColor = (status: DivisionStatus) => {
    switch (status) {
      case DivisionStatus.UPCOMING:
        return 'bg-blue-100 text-blue-800';
      case DivisionStatus.ACTIVE:
        return 'bg-green-100 text-green-800';
      case DivisionStatus.COMPLETED:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!groupByCategory) {
    return (
      <div className="space-y-4">
        {divisions.map(division => (
          <div key={division.id} className="bg-white shadow rounded-lg p-4">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="text-base font-semibold text-gray-900">{division.name}</h4>
                <p className="text-sm text-gray-500 mt-1">
                  Level {division.level} - {division.category}
                </p>
              </div>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(
                  division.status,
                )}`}
              >
                {division.status}
              </span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Max Teams:</span>{' '}
                <span className="font-medium text-gray-900">{division.maxTeams}</span>
              </div>
              <div>
                <span className="text-gray-500">Promotion:</span>{' '}
                <span className="font-medium text-gray-900">{division.teamsToPromote}</span>
              </div>
              <div>
                <span className="text-gray-500">Relegation:</span>{' '}
                <span className="font-medium text-gray-900">{division.teamsToRelegate}</span>
              </div>
            </div>
            {renderDivisionActions(division)}
          </div>
        ))}
      </div>
    );
  }

  // Group divisions by category
  const divisionsByCategory = divisions.reduce((acc, division) => {
    if (!acc[division.category]) {
      acc[division.category] = [];
    }
    acc[division.category].push(division);
    return acc;
  }, {} as Record<Category, Division[]>);

  // Sort divisions within each category by level
  Object.keys(divisionsByCategory).forEach(category => {
    divisionsByCategory[category as Category].sort((a, b) => a.level - b.level);
  });

  // Compact banner variant — divisions grouped by category, then by ladder
  // (divisionGroupId / baseName), with a subgroup heading per ladder.
  if (compact) {
    return (
      <div className="space-y-6">
        {Object.entries(divisionsByCategory).map(([category, categoryDivisions]) => {
          // Build ladder groups within this category
          const ladderMap = new Map<string, Division[]>();
          for (const d of categoryDivisions) {
            const key = d.divisionGroupId ?? d.baseName ?? d.id;
            const bucket = ladderMap.get(key) ?? [];
            if (!ladderMap.has(key)) ladderMap.set(key, bucket);
            bucket.push(d);
          }
          const ladders = Array.from(ladderMap.values()).sort((a, b) => a[0].level - b[0].level);

          return (
            <div key={category}>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                {category}
              </h4>
              <div className="space-y-3">
                {ladders.map(ladderDivs => {
                  const groupKey =
                    ladderDivs[0].divisionGroupId ?? ladderDivs[0].baseName ?? ladderDivs[0].id;
                  const ladderLabel = ladderDivs[0].baseName ?? ladderDivs[0].name;

                  return (
                    <div key={groupKey} className="bg-white shadow rounded-md overflow-hidden">
                      {/* Division (ladder) header: label + the ONE status for the
                          whole division, plus the per-ladder Manage + Edit actions.
                          Status and Edit belong to the division, not each tier. */}
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">
                            {ladderLabel}
                          </span>
                          <span
                            className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(
                              ladderDivs[0].status,
                            )}`}
                          >
                            {ladderDivs[0].status}
                          </span>
                        </div>
                        {canManage && leagueId && seasonId && (
                          <div className="shrink-0 flex items-center gap-2">
                            <Link
                              to={manageTeamsHref(ladderDivs[0])}
                              className="px-3 py-1 rounded-md text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                            >
                              Manage
                            </Link>
                            <Link
                              to={`/leagues/${leagueId}/seasons/${seasonId}/divisions/${ladderDivs[0].id}`}
                              className="px-3 py-1 rounded-md text-xs font-medium text-primary-600 border border-primary-600 hover:bg-primary-50 transition-colors"
                            >
                              Edit
                            </Link>
                          </div>
                        )}
                      </div>
                      <div className="divide-y divide-gray-200">
                        {ladderDivs.map(division => (
                          <div
                            key={division.id}
                            className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-gray-50"
                          >
                            <div className="min-w-0 flex-1">
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {division.name}
                              </span>
                              <div className="mt-0.5 text-xs text-gray-500">
                                Level {division.level} · Max {division.maxTeams} · ↑
                                {division.teamsToPromote} ↓{division.teamsToRelegate}
                                {division.genderRestriction &&
                                  division.genderRestriction !== 'none' &&
                                  ` · ${division.genderRestriction}`}
                              </div>
                            </div>
                            {leagueId && seasonId && (
                              <Link
                                to={`/leagues/${leagueId}/seasons/${seasonId}/divisions/${division.id}/standings`}
                                className="shrink-0 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                              >
                                Standings
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {Object.entries(divisionsByCategory).map(([category, categoryDivisions]) => (
        <div key={category}>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{category}</h3>
          <div className="space-y-3">
            {categoryDivisions.map(division => (
              <div
                key={division.id}
                className="bg-white shadow rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-base font-semibold text-gray-900">{division.name}</h4>
                    <p className="text-sm text-gray-500 mt-1">Level {division.level}</p>
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(
                      division.status,
                    )}`}
                  >
                    {division.status}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Max Teams:</span>{' '}
                    <span className="font-medium text-gray-900">{division.maxTeams}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Promotion:</span>{' '}
                    <span className="font-medium text-gray-900">{division.teamsToPromote}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Relegation:</span>{' '}
                    <span className="font-medium text-gray-900">{division.teamsToRelegate}</span>
                  </div>
                  {division.ageRestriction && (
                    <div>
                      <span className="text-gray-500">Age:</span>{' '}
                      <span className="font-medium text-gray-900">
                        {division.ageRestriction.minimum && `${division.ageRestriction.minimum}+`}
                        {division.ageRestriction.minimum &&
                          division.ageRestriction.maximum &&
                          ' - '}
                        {division.ageRestriction.maximum && `${division.ageRestriction.maximum}`}
                      </span>
                    </div>
                  )}
                </div>
                {division.genderRestriction && (
                  <div className="mt-3 text-sm">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                      {division.genderRestriction}
                    </span>
                  </div>
                )}
                {renderDivisionActions(division)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DivisionList;
